// Payroll API helper — namespaced under /api/payroll and uses our cookie session
// (no Bearer header). Drop-in replacement for the standalone payroll's api.ts.

export const BASE_URL = "/api/payroll";

export interface ApiResponse<T> {
  data: T;
  message?: string;
  error?: string;
  success: boolean;
}

async function request<T>(
  url: string,
  options: RequestInit = {},
): Promise<ApiResponse<T>> {
  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...((options.headers as Record<string, string>) || {}),
  };

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${url}`, {
      credentials: "include",
      ...options,
      headers,
    });
  } catch {
    return { success: false, data: null as unknown as T, error: "Network error" };
  }

  if (response.status === 401) {
    window.location.href = "/login";
    return { success: false, data: null as unknown as T, error: "Session expired" };
  }

  if (!response.ok) {
    const errorBody = (await response.json().catch(() => ({}))) as Record<string, unknown>;
    return {
      success: false,
      data: null as unknown as T,
      error: (errorBody.error as string) || (errorBody.message as string) || `Error ${response.status}`,
    };
  }

  const data = (await response.json()) as ApiResponse<T> | T;
  if (data && typeof data === "object" && "success" in (data as Record<string, unknown>)) {
    return data as ApiResponse<T>;
  }
  return { success: true, data: data as T };
}

export const api = {
  get:    <T,>(url: string)                     => request<T>(url, { method: "GET" }),
  post:   <T,>(url: string, body?: unknown)     => request<T>(url, { method: "POST",   body: body !== undefined ? JSON.stringify(body) : undefined }),
  put:    <T,>(url: string, body?: unknown)     => request<T>(url, { method: "PUT",    body: body !== undefined ? JSON.stringify(body) : undefined }),
  patch:  <T,>(url: string, body?: unknown)     => request<T>(url, { method: "PATCH",  body: body !== undefined ? JSON.stringify(body) : undefined }),
  delete: <T,>(url: string)                     => request<T>(url, { method: "DELETE" }),
};

export default api;
